/**
 * OPFS-backed model storage.
 *
 * The on-device LLM file is multi-gigabyte; OPFS is the right home — it's
 * the only browser persistence with the quota to fit it, it survives reload,
 * and the directory is private to this origin. We stream the download
 * straight into an OPFS file (fetch's body stream piped to the writable
 * stream) so the model never lives entirely in JS memory.
 *
 * Cancellation is supported via AbortSignal; the partial file is removed
 * when the user cancels so a subsequent retry starts clean.
 */

import { DebugLog } from "../debug/log";
import { effectiveModelConfig, type Settings } from "./types";

const TAG = "data.modelStore";
const ROOT_DIR = "models";

export interface ModelInfo {
  fileName: string;
  sizeBytes: number;
}

export interface DownloadProgress {
  bytesDownloaded: number;
  bytesTotal: number | null;
}

async function getModelsDir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) {
    return null;
  }
  const root = await navigator.storage.getDirectory();
  try {
    return await root.getDirectoryHandle(ROOT_DIR, { create });
  } catch {
    return null;
  }
}

export async function isOpfsAvailable(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) {
    return false;
  }
  try {
    await navigator.storage.getDirectory();
    return true;
  } catch {
    return false;
  }
}

export async function getInstalledModel(fileName: string): Promise<ModelInfo | null> {
  const dir = await getModelsDir(false);
  if (!dir) return null;
  try {
    const handle = await dir.getFileHandle(fileName);
    const file = await handle.getFile();
    return { fileName, sizeBytes: file.size };
  } catch {
    return null;
  }
}

export async function deleteInstalledModel(fileName: string): Promise<void> {
  const dir = await getModelsDir(false);
  if (!dir) return;
  try {
    await dir.removeEntry(fileName);
  } catch {
    // not present
  }
}

export async function openModelStream(fileName: string): Promise<ReadableStream<Uint8Array> | null> {
  const dir = await getModelsDir(false);
  if (!dir) return null;
  try {
    const handle = await dir.getFileHandle(fileName);
    const file = await handle.getFile();
    return file.stream();
  } catch {
    return null;
  }
}

export interface ModelProbe {
  exists: boolean;
  sizeBytes: number | null;
  magicHex: string | null;
  magicAscii: string | null;
}

/**
 * Reads the first 32 bytes of the OPFS model file and returns a hex + ASCII
 * preview. Used by the Debug screen to identify what's actually on disk —
 * a real .litertlm has a custom magic, .task is a ZIP (`PK\x03\x04`), TFLite
 * is `\x18\x00\x00\x00TFL3`, and an HTML page (the failure mode where the
 * user pasted a repo URL) starts with `<!DOCTYPE` or `<html`.
 */
export async function probeModelMagic(fileName: string): Promise<ModelProbe> {
  const dir = await getModelsDir(false);
  if (!dir) return { exists: false, sizeBytes: null, magicHex: null, magicAscii: null };
  try {
    const handle = await dir.getFileHandle(fileName);
    const file = await handle.getFile();
    const slice = file.slice(0, 32);
    const buf = new Uint8Array(await slice.arrayBuffer());
    const magicHex = Array.from(buf)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(" ");
    const magicAscii = Array.from(buf)
      .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : "."))
      .join("");
    return { exists: true, sizeBytes: file.size, magicHex, magicAscii };
  } catch {
    return { exists: false, sizeBytes: null, magicHex: null, magicAscii: null };
  }
}

/**
 * Streams a Hugging Face download into OPFS. `onProgress` fires with the
 * running byte counts so the UI can render a live progress bar. Throws on
 * network errors, HTTP non-2xx, or AbortSignal.aborted.
 */
export async function downloadModel(input: {
  url: string;
  fileName: string;
  authToken?: string;
  signal: AbortSignal;
  onProgress?: (p: DownloadProgress) => void;
}): Promise<ModelInfo> {
  DebugLog.i(TAG, `download starting`, `fileName=${input.fileName} url=${input.url} hasAuth=${Boolean(input.authToken?.trim())}`);
  const dir = await getModelsDir(true);
  if (!dir) {
    DebugLog.e(TAG, "OPFS not available");
    throw new Error(
      "This browser doesn't expose OPFS. Try a recent Chrome, Edge, or Safari 18+.",
    );
  }

  const headers: Record<string, string> = {};
  if (input.authToken?.trim()) {
    headers.Authorization = `Bearer ${input.authToken.trim()}`;
  }
  const response = await fetch(input.url, { headers, signal: input.signal });
  DebugLog.i(
    TAG,
    `fetch responded`,
    `status=${response.status} content-type=${response.headers.get("content-type") ?? "?"} content-length=${response.headers.get("content-length") ?? "?"}`,
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const detail = text.slice(0, 200);
    throw new Error(`${response.status} ${response.statusText} from model host${detail ? `: ${detail}` : ""}`);
  }
  if (!response.body) {
    throw new Error("Response had no body.");
  }

  // Guard against the user pasting a repo-root URL: HF returns HTML (text/html)
  // for those, which would silently install a useless ~150 KB blob — refuse early.
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("text/html")) {
    throw new Error(
      "URL returned HTML, not a model file. You probably pasted a repo URL — use the file's /resolve/main/<name>.litertlm form.",
    );
  }

  const totalHeader = response.headers.get("content-length");
  const bytesTotal = totalHeader ? Number.parseInt(totalHeader, 10) : null;

  // Write to a temp filename first; only rename to the final name on success.
  const tempName = `${input.fileName}.partial`;
  const fileHandle = await dir.getFileHandle(tempName, { create: true });
  const writable = await fileHandle.createWritable();

  const reader = response.body.getReader();
  let received = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (input.signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      if (value && value.length) {
        await writable.write(value);
        received += value.length;
        input.onProgress?.({ bytesDownloaded: received, bytesTotal });
      }
    }
    await writable.close();
  } catch (e) {
    try {
      await writable.close();
    } catch {
      /* ignore close error */
    }
    try {
      await dir.removeEntry(tempName);
    } catch {
      /* ignore */
    }
    throw e;
  }

  // Replace any existing model with the freshly downloaded one.
  try {
    await dir.removeEntry(input.fileName);
  } catch {
    /* not present */
  }
  // OPFS doesn't expose `rename`; copy-then-delete via streaming.
  await renameOpfsFile(dir, tempName, input.fileName);

  const finalFile = await (await dir.getFileHandle(input.fileName)).getFile();
  DebugLog.i(TAG, `download finished`, `fileName=${input.fileName} sizeBytes=${finalFile.size}`);
  // Probe the magic right after install so the debug log captures it without
  // requiring the user to navigate anywhere.
  const probe = await probeModelMagic(input.fileName);
  DebugLog.i(TAG, `installed file probe`, `magicHex=${probe.magicHex} magicAscii=${probe.magicAscii}`);
  return { fileName: input.fileName, sizeBytes: finalFile.size };
}

async function renameOpfsFile(
  dir: FileSystemDirectoryHandle,
  fromName: string,
  toName: string,
): Promise<void> {
  // Per the File System Access API draft, `move` exists on FileSystemFileHandle
  // in newer browsers; fall back to copy+delete otherwise.
  const fromHandle = await dir.getFileHandle(fromName);
  const maybeMove = (fromHandle as unknown as { move?: (newName: string) => Promise<void> }).move;
  if (typeof maybeMove === "function") {
    await maybeMove.call(fromHandle, toName);
    return;
  }
  const sourceFile = await fromHandle.getFile();
  const sourceStream = sourceFile.stream();
  const targetHandle = await dir.getFileHandle(toName, { create: true });
  const targetWritable = await targetHandle.createWritable();
  await sourceStream.pipeTo(targetWritable);
  await dir.removeEntry(fromName);
}

export function fileNameFor(settings: Settings): string {
  return effectiveModelConfig(settings).fileName;
}
